import {
  ChakraFlex,
  FAIconName,
  FireflyButton,
  ModalV2,
  useToastMessage,
  Text,
} from '@equip.health/ui';
import { useUpdateDischargePlanStatusMutation } from '~/store/services/clinical-data/dischargePlan';
import { DischargePlanStatuses } from '../common/dischargePlanConstants';
import { DischargePlanEvents } from '~/analytics/dischargePlanEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';

interface ReviseDischargePlanModalProps {
  dischargePlanId: string;
  isOpen: boolean;
  onClose: () => void;
}

const ReviseDischargePlanModal = ({
  dischargePlanId,
  isOpen,
  onClose,
}: ReviseDischargePlanModalProps) => {
  const [
    updateDischargePlanStatus,
    { isLoading: isUpdateDischargePlanStatusLoading },
  ] = useUpdateDischargePlanStatusMutation();
  const { createToast } = useToastMessage();
  const { trackEvent } = useAnalytics();

  const handleSuccess = () => {
    createToast({
      icon: FAIconName.checkRegular,
      id: 'revise-discharge-plan-success',
      text: 'Successfully revised discharge plan',
      variant: 'success',
    });
  };

  const handleFailure = () => {
    createToast({
      icon: FAIconName.exclamationTriangleRegular,
      id: 'revise-discharge-plan-error',
      text: "We're sorry, we were unable to update the discharge plan status. Please try again later",
      variant: 'critical',
    });
  };

  return (
    <ModalV2
      footerContent={
        <ChakraFlex
          alignItems="center"
          justifyContent="space-between"
          marginTop="10px"
          width="100%"
        >
          <FireflyButton
            id="revise-discharge-plan-cancel"
            isLoading={isUpdateDischargePlanStatusLoading}
            leftIcon={FAIconName.timesRegular}
            onClick={() => {
              trackEvent(
                DischargePlanEvents.clickOnCancelButtonInReviseDischargePlanModal,
              );
              onClose();
            }}
            variant="secondary"
          >
            Cancel
          </FireflyButton>
          <FireflyButton
            id="revise-discharge-plan-confirm"
            isLoading={isUpdateDischargePlanStatusLoading}
            leftIcon={FAIconName.checkRegular}
            onClick={() => {
              trackEvent(
                DischargePlanEvents.clickOnConfirmButtonInReviseDischargePlanModal,
              );
              updateDischargePlanStatus({
                dischargePlanId,
                statusCode: DischargePlanStatuses.PREPPING.code,
              })
                .unwrap()
                .then(() => handleSuccess())
                .catch(() => handleFailure())
                .finally(() => onClose());
            }}
            variant="primary"
          >
            Confirm
          </FireflyButton>
        </ChakraFlex>
      }
      isOpen={isOpen}
      maxWidth="512px"
      minWidth="512px"
      onClose={() => {
        trackEvent(DischargePlanEvents.clickOnXIconInReviseDischargePlanModal);
        onClose();
      }}
      title="Revise discharge plan?"
    >
      <ChakraFlex flexDirection="column" gap="10px">
        <Text>
          This will invalidate the current signature on the discharge plan.
        </Text>
        <Text>
          When the patient is ready to discharge, the discharge plan will need
          to be signed and finalized again.
        </Text>
      </ChakraFlex>
    </ModalV2>
  );
};

export default ReviseDischargePlanModal;
